import { Fragment, useCallback, useEffect, useRef, useState } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import {
  ArrowLeft, SpeakerHigh, Check, X, ArrowClockwise,
  Lightning, Sparkle, ArrowRight, Target, CaretLeft, CaretRight,
} from "@phosphor-icons/react";
import { Mascot } from "@/components/styleguide/Mascot";
import { BloomButton } from "@/components/styleguide/ui/BloomButton";
import { BloomBadge } from "@/components/styleguide/ui/BloomBadge";
import { cn } from "@/lib/utils";
import { api, speakWord } from "./api";

/* ── Skill metadata ────────────────────────────────────── */

const SKILL_META = {
  meaning:  { label: "Meaning",    badge: "primary" },
  synonym:  { label: "Synonyms",   badge: "teal" },
  antonym:  { label: "Antonyms",   badge: "blue" },
  sentence: { label: "Sentences",  badge: "primary" },
  mixed:    { label: "Mixed Quiz", badge: "primary" },
};

const LABELS = ["A", "B", "C", "D"];

const ENCOURAGEMENTS = [
  "Brilliant — you've got it!",
  "Superstar!",
  "That's the one!",
  "You're on fire!",
  "Amazing work!",
  "Nailed it!",
];

const NEAR_MISSES = [
  "So close — you'll get the next one!",
  "Nearly there — keep going!",
  "Good try — you're learning!",
  "Not quite, but you're growing!",
];

function pickRandom(arr) {
  return arr[Math.floor(Math.random() * arr.length)];
}

/* ── Option button ─────────────────────────────────────── */

function OptionButton({ label, text, state, onClick, index }) {
  const base =
    "flex items-start gap-3 rounded-2xl border-2 p-4 text-left transition-all duration-200 w-full min-h-[3.5rem]";
  const styles = {
    idle:     "border-border bg-card hover:border-primary/40 hover:-translate-y-0.5 active:scale-[0.98] cursor-pointer",
    correct:  "border-teal bg-teal/12",
    wrong:    "border-destructive bg-destructive/10",
    revealed: "border-teal bg-teal/10",
    done:     "border-border bg-card/60 opacity-45 cursor-default",
  };
  const badge = {
    idle:     "bg-muted text-muted-foreground",
    correct:  "bg-teal text-white",
    wrong:    "bg-destructive text-white",
    revealed: "bg-teal/80 text-white",
    done:     "bg-muted text-muted-foreground",
  };

  return (
    <motion.button
      initial={{ opacity: 0, y: 12 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay: 0.08 + index * 0.05 }}
      onClick={onClick}
      disabled={state !== "idle"}
      className={cn(base, styles[state])}
      data-testid={`option-${index}`}
    >
      <span
        className={cn(
          "flex h-8 w-8 shrink-0 items-center justify-center rounded-full font-display text-sm font-black transition-colors",
          badge[state],
        )}
      >
        {state === "correct" || state === "revealed" ? (
          <Check size={15} weight="bold" />
        ) : state === "wrong" ? (
          <X size={15} weight="bold" />
        ) : (
          label
        )}
      </span>
      <span className="pt-1 text-[15px] font-semibold leading-snug text-foreground">
        {text}
      </span>
    </motion.button>
  );
}

/* ── Sentence with styled blank ────────────────────────── */

function BlankSentence({ text }) {
  const parts = text.split("______");
  return (
    <>
      &ldquo;
      {parts.map((part, i) => (
        <Fragment key={i}>
          {part}
          {i < parts.length - 1 && (
            <span className="mx-1 inline-block min-w-[3.5rem] border-b-[3px] border-dashed border-primary align-baseline" />
          )}
        </Fragment>
      ))}
      &rdquo;
    </>
  );
}

/* ── Difficulty badge variant map ──────────────────────── */
const DIFF_V = { easy: "success", medium: "warning", hard: "primary" };

/* ── Word help card (shown inside the swipeable sheet) ─── */
function WordHelpCard({ word }) {
  return (
    <div className="space-y-4 pb-2">
      <div className="flex flex-wrap items-center gap-3">
        <h2 className="font-display text-3xl font-black capitalize text-foreground">
          {word.word}
        </h2>
        <button
          onClick={() => speakWord(word.word)}
          className="flex h-11 w-11 items-center justify-center rounded-full bg-primary text-primary-foreground shadow-glow active:scale-90"
          aria-label={`Say ${word.word}`}
        >
          <SpeakerHigh size={20} weight="fill" />
        </button>
      </div>
      <div className="flex flex-wrap gap-1.5">
        {word.word_type  && <BloomBadge variant="neutral"                   className="px-2.5 py-1 text-xs capitalize">{word.word_type}</BloomBadge>}
        {word.difficulty && <BloomBadge variant={DIFF_V[word.difficulty] || "neutral"} className="px-2.5 py-1 text-xs capitalize">{word.difficulty}</BloomBadge>}
      </div>
      {word.meaning && (
        <p className="text-base font-semibold leading-relaxed text-foreground/90">{word.meaning}</p>
      )}
      {word.synonyms?.length > 0 && (
        <div>
          <p className="mb-2 text-[11px] font-extrabold uppercase tracking-wider text-muted-foreground">Synonyms</p>
          <div className="flex flex-wrap gap-2">
            {word.synonyms.map((s) => (
              <span key={s} className="rounded-full bg-teal/15 px-3 py-1.5 text-sm font-extrabold text-teal-ink">{s}</span>
            ))}
          </div>
        </div>
      )}
      {word.antonyms?.length > 0 && (
        <div>
          <p className="mb-2 text-[11px] font-extrabold uppercase tracking-wider text-muted-foreground">Antonyms</p>
          <div className="flex flex-wrap gap-2">
            {word.antonyms.map((a) => (
              <span key={a} className="rounded-full bg-destructive/10 px-3 py-1.5 text-sm font-extrabold text-destructive">{a}</span>
            ))}
          </div>
        </div>
      )}
      {word.image_url && (
        <div className="overflow-hidden rounded-2xl">
          {word.image_label && (
            <div className="bg-teal px-3 py-1.5 text-center">
              <span className="text-[11px] font-extrabold uppercase tracking-wider text-white">{word.image_label}</span>
            </div>
          )}
          <img src={word.image_url} alt={word.word} className="w-full object-contain" style={{ aspectRatio: "3/4" }} />
        </div>
      )}
      {word.sentences?.length > 0 && (
        <div>
          <p className="mb-2 text-[11px] font-extrabold uppercase tracking-wider text-muted-foreground">In a sentence</p>
          {word.sentences.slice(0, 2).map((s, i) => (
            <p key={i} className="mb-2 rounded-2xl bg-muted/60 px-4 py-3 text-sm font-semibold text-foreground/85">{s}</p>
          ))}
        </div>
      )}
    </div>
  );
}

/* ── Swipeable quiz help bottom sheet ──────────────────── */
function QuizHelpSheet({ words, initialIndex = 0, onClose }) {
  const [idx,     setIdx]     = useState(initialIndex);
  const [dragDir, setDragDir] = useState(0);

  const canPrev = idx > 0;
  const canNext = idx < words.length - 1;

  const goTo = (newIdx, dir) => { setDragDir(dir); setIdx(newIdx); };

  const handleDragEnd = (_, info) => {
    if      (info.offset.x < -50 && canNext) goTo(idx + 1,  1);
    else if (info.offset.x >  50 && canPrev) goTo(idx - 1, -1);
  };

  const variants = {
    enter:  (dir) => ({ x: dir > 0 ? "100%" : "-100%", opacity: 0   }),
    center:          { x: 0,                             opacity: 1   },
    exit:   (dir) => ({ x: dir > 0 ? "-100%" : "100%", opacity: 0   }),
  };

  return (
    <>
      <motion.div
        initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
        className="fixed inset-0 z-40 bg-black/40 backdrop-blur-sm"
        onClick={onClose}
      />
      <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center pointer-events-none p-3 sm:p-6">
        <motion.div
          initial={{ y: "100%", opacity: 0 }} animate={{ y: 0, opacity: 1 }} exit={{ y: "100%", opacity: 0 }}
          transition={{ type: "spring", damping: 28, stiffness: 300 }}
          className="pointer-events-auto w-full max-w-2xl h-[80svh] flex flex-col rounded-3xl bg-card shadow-2xl"
        >
        {/* Sheet header */}
        <div className="flex shrink-0 items-center justify-between border-b border-border px-5 py-4">
          <div>
            <p className="font-display text-sm font-black text-foreground">Explore these words</p>
            {words.length > 1 && (
              <p className="text-[10px] font-semibold text-muted-foreground">
                Swipe or tap dots to browse all {words.length} words
              </p>
            )}
          </div>
          <button
            onClick={onClose}
            className="flex h-9 w-9 items-center justify-center rounded-full bg-muted text-muted-foreground hover:bg-border"
            aria-label="Close"
          >
            <X size={15} weight="bold" />
          </button>
        </div>

        {/* Swipeable word card */}
        <div className="relative min-h-0 flex-1 overflow-x-hidden overflow-y-auto">
          <AnimatePresence mode="wait" custom={dragDir}>
            <motion.div
              key={idx}
              custom={dragDir}
              variants={variants}
              initial="enter"
              animate="center"
              exit="exit"
              transition={{ duration: 0.22, ease: "easeInOut" }}
              drag={words.length > 1 ? "x" : false}
              dragConstraints={{ left: 0, right: 0 }}
              dragElastic={0.15}
              onDragEnd={handleDragEnd}
              className="min-h-full cursor-grab px-5 py-4 active:cursor-grabbing"
              style={{ touchAction: "pan-y" }}
            >
              <WordHelpCard word={words[idx]} />
            </motion.div>
          </AnimatePresence>
        </div>

        {/* Dots + prev/next — only when multiple words */}
        {words.length > 1 && (
          <div className="flex shrink-0 items-center justify-between border-t border-border px-5 py-3">
            <button
              onClick={() => canPrev && goTo(idx - 1, -1)}
              disabled={!canPrev}
              className="flex items-center gap-1 text-xs font-extrabold text-primary-strong disabled:opacity-30"
            >
              <CaretLeft size={12} weight="bold" /> Prev
            </button>
            <div className="flex gap-1.5">
              {words.map((_, i) => (
                <button
                  key={i}
                  onClick={() => goTo(i, i > idx ? 1 : -1)}
                  className={cn(
                    "h-2 rounded-full transition-all",
                    i === idx ? "w-5 bg-primary" : "w-2 bg-border hover:bg-primary/40",
                  )}
                />
              ))}
            </div>
            <button
              onClick={() => canNext && goTo(idx + 1, 1)}
              disabled={!canNext}
              className="flex items-center gap-1 text-xs font-extrabold text-primary-strong disabled:opacity-30"
            >
              Next <CaretRight size={12} weight="bold" />
            </button>
          </div>
        )}
        </motion.div>
      </div>
    </>
  );
}

/* ── Main QuizPage ─────────────────────────────────────── */

export default function QuizPage() {
  const { childId, skill } = useParams();
  const [searchParams] = useSearchParams();
  const planId       = searchParams.get("plan_id") || null;
  const focusWordIds = searchParams.get("focus_word_ids") || null;
  const isRevision   = searchParams.get("revision") === "true";
  const countParam   = searchParams.get("count");
  const helpEnabled  = searchParams.get("help") === "true";
  const navigate = useNavigate();
  const meta = SKILL_META[skill] || SKILL_META.meaning;

  const [quiz, setQuiz] = useState(null);
  const [idx, setIdx] = useState(0);
  const [selected, setSelected] = useState(null);
  const [phase, setPhase] = useState("loading"); // loading | playing | feedback | results
  const [score, setScore] = useState(0);
  const [streak, setStreak] = useState(0);
  const [bestStreak, setBestStreak] = useState(0);
  const [history, setHistory] = useState([]);
  const [error,         setError]         = useState("");
  const [wrongWordIds,  setWrongWordIds]  = useState([]);
  const [practiceMode, setPracticeMode]  = useState(false);
  const [practiceIds,  setPracticeIds]   = useState(null);
  const [helpOpen,     setHelpOpen]      = useState(false);
  const [helpInitIdx,  setHelpInitIdx]   = useState(0);
  const timerRef = useRef(null);

  /* ── Load quiz ──────────────────────────────────────── */

  const loadQuiz = useCallback(() => {
    setPhase("loading");
    setIdx(0);
    setScore(0);
    setStreak(0);
    setBestStreak(0);
    setSelected(null);
    setHistory([]);
    setError("");
    setQuiz(null);
    if (!practiceMode) { setWrongWordIds([]); setPracticeIds(null); }

    const qlimit = practiceIds   ? 9
                 : isRevision    ? Math.max(1, Math.min(parseInt(countParam) || 20, 50))
                 : planId        ? 20   // backend uses plan.quiz_count — this is ignored
                 : 10;
    const focusParam = practiceIds ? `&focus_word_ids=${practiceIds}` : focusWordIds ? `&focus_word_ids=${focusWordIds}` : ``;
    api(`/quiz/questions?child_id=${childId}&skill=${skill}&limit=${qlimit}${planId ? `&plan_id=${planId}` : ``}${focusParam}${isRevision ? `&revision=true` : ``}`)
      .then((data) => {
        if (!data.questions?.length) {
          setError("Not enough words for a quiz yet — learn some words first!");
          return;
        }
        setQuiz(data);
        setPhase("playing");
      })
      .catch((e) => setError(e.message || "Could not load the quiz"));
  }, [childId, skill, planId]);

  useEffect(() => {
    loadQuiz();
    return () => clearTimeout(timerRef.current);
  }, [loadQuiz]);

  const question = quiz?.questions?.[idx];
  const total = quiz?.questions?.length || 0;

  /* ── Handle answer ──────────────────────────────────── */

  const handleSelect = useCallback(
    (optionIdx) => {
      if (phase !== "playing" || selected !== null) return;

      const q = quiz.questions[idx];
      const isCorrect = optionIdx === q.correct;

      setSelected(optionIdx);
      setPhase("feedback");

      const newStreak = isCorrect ? streak + 1 : 0;
      if (isCorrect) setScore((s) => s + 1);
      setStreak(newStreak);
      if (newStreak > bestStreak) setBestStreak(newStreak);

      setHistory((prev) => [
        ...prev,
        {
          word: q.word,
          correct: isCorrect,
          skill: q.skill,
          correctAnswer: q.options[q.correct],
        },
      ]);

      // Speak word for reinforcement
      if (q.skill !== "sentence") {
        setTimeout(() => speakWord(q.word), 350);
      }

      // Record in backend (fire-and-forget)
      api("/quiz/answer", {
        method: "POST",
        body: {
          quiz_id: quiz.quiz_id,
          word_id: q.word_id,
          skill: q.skill,
          selected: optionIdx,
          correct: isCorrect,
        },
      }).catch(() => {});

      // Auto-advance — suppressed when help is enabled and answer is wrong
      // (parent wants child to explore words before moving on)
      if (!(helpEnabled && !isCorrect)) {
        timerRef.current = setTimeout(() => {
          if (idx < total - 1) {
            setIdx((i) => i + 1);
            setSelected(null);
            setPhase("playing");
          } else {
            api("/quiz/complete", {
              method: "POST",
              body: { quiz_id: quiz.quiz_id },
            }).catch(() => {});
            setPhase("results");
          }
        }, 1600);
      }
    },
    [phase, selected, quiz, idx, total, streak, bestStreak],
  );

  /* ── Manual next (used when help is enabled + wrong answer) ── */
  const handleNext = useCallback(() => {
    setHelpOpen(false);
    if (idx < total - 1) {
      setIdx((i) => i + 1);
      setSelected(null);
      setPhase("playing");
    } else {
      api("/quiz/complete", { method: "POST", body: { quiz_id: quiz.quiz_id } }).catch(() => {});
      setPhase("results");
    }
  }, [idx, total, quiz]);

  /* ── Error state ────────────────────────────────────── */

  if (error) {
    return (
      <div className="flex min-h-[100svh] flex-col items-center justify-center bg-gradient-to-b from-[#FFF7EF] to-[#DCF3EC] px-6 text-center">
        <Mascot size={80} mood="encouraging" animated={false} />
        <h2 className="mt-4 font-display text-2xl font-black text-foreground">
          Hmm…
        </h2>
        <p className="mt-2 max-w-sm text-base font-semibold text-muted-foreground">
          {error}
        </p>
        <BloomButton
          className="mt-6"
          onClick={() => navigate(`/vocab/child/${childId}`)}
        >
          Back to home
        </BloomButton>
      </div>
    );
  }

  /* ── Loading state ──────────────────────────────────── */

  if (phase === "loading") {
    return (
      <div className="flex min-h-[100svh] flex-col items-center justify-center bg-gradient-to-b from-[#FFF7EF] to-[#DCF3EC]">
        <motion.div
          animate={{ rotate: [0, 8, -8, 0] }}
          transition={{ duration: 1.5, repeat: Infinity }}
        >
          <Mascot size={80} mood="happy" animated={false} />
        </motion.div>
        <p className="mt-4 font-display text-lg font-bold text-muted-foreground">
          Getting your quiz ready…
        </p>
      </div>
    );
  }

  /* ── Results state ──────────────────────────────────── */

  if (phase === "results") {
    const pct = total > 0 ? Math.round((score / total) * 100) : 0;
    const mood =
      pct >= 80 ? "celebrating" : pct >= 50 ? "happy" : "encouraging";
    const title =
      pct >= 80 ? "Brilliant!" : pct >= 50 ? "Well done!" : "Keep growing!";
    const subtitle =
      pct >= 80
        ? "You really know your words!"
        : pct >= 50
          ? "Great effort — you're getting there!"
          : "Every quiz helps your words grow. Try again!";

    const wrongOnes = history.filter((h) => !h.correct);

    return (
      <div
        className="flex min-h-[100svh] flex-col items-center justify-center bg-gradient-to-b from-[#FFF7EF] to-[#DCF3EC] px-6 pb-12 pt-8 text-center"
        data-testid="quiz-results"
      >
        <motion.div
          initial={{ scale: 0.5, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          transition={{ type: "spring", stiffness: 200, damping: 15 }}
        >
          <Mascot size={110} mood={mood} animated={false} />
        </motion.div>

        <motion.h1
          initial={{ y: 20, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          transition={{ delay: 0.2 }}
          className="mt-4 font-display text-3xl font-black text-foreground sm:text-4xl"
        >
          {title}
        </motion.h1>

        <motion.div
          initial={{ y: 20, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          transition={{ delay: 0.35 }}
          className="mt-3 flex items-baseline gap-1"
        >
          <span className="font-display text-5xl font-black text-primary-strong">
            {score}
          </span>
          <span className="text-2xl font-bold text-muted-foreground">
            / {total}
          </span>
        </motion.div>

        <motion.p
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.5 }}
          className="mt-2 text-base font-semibold text-muted-foreground"
        >
          {subtitle}
        </motion.p>

        {bestStreak >= 3 && (
          <motion.div
            initial={{ scale: 0 }}
            animate={{ scale: 1 }}
            transition={{ delay: 0.6, type: "spring" }}
            className="mt-3 flex items-center gap-1.5 rounded-full bg-secondary/25 px-4 py-2"
          >
            <Lightning size={16} weight="fill" className="text-secondary-ink" />
            <span className="text-sm font-extrabold text-secondary-ink">
              Best streak: {bestStreak} in a row!
            </span>
          </motion.div>
        )}

        {wrongOnes.length > 0 && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.7 }}
            className="mt-6 w-full max-w-sm"
          >
            <p className="text-[11px] font-extrabold uppercase tracking-wider text-muted-foreground">
              Words to revisit
            </p>
            <div className="mt-2 flex flex-wrap justify-center gap-2">
              {wrongOnes.map((h, i) => (
                <span
                  key={i}
                  className="rounded-full bg-destructive/10 px-3.5 py-1.5 text-sm font-extrabold text-destructive"
                >
                  {h.word}
                </span>
              ))}
            </div>
          </motion.div>
        )}

        <motion.div
          initial={{ y: 20, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          transition={{ delay: 0.8 }}
          className="mt-8 flex flex-wrap justify-center gap-3"
        >
          <BloomButton
            variant="outline"
            leftIcon={ArrowClockwise}
            onClick={loadQuiz}
            data-testid="quiz-retry-btn"
          >
            Try again
          </BloomButton>
          <BloomButton
            onClick={() => navigate(planId
              ? `/vocab/child/${childId}/plans`
              : `/vocab/child/${childId}`
            )}
            data-testid="quiz-done-btn"
          >
            {planId ? "Back to plans" : isRevision ? "Back home" : "All done!"}
          </BloomButton>
        </motion.div>
      </div>
    );
  }

  /* ── Playing / Feedback state ───────────────────────── */

  const isLongOption = question?.options?.some((o) => o.length > 10);
  const isSentence = question?.skill === "sentence";

  const getState = (i) => {
    if (selected === null) return "idle";
    if (i === selected && i === question.correct) return "correct";
    if (i === selected && i !== question.correct) return "wrong";
    if (i === question.correct) return "revealed";
    return "done";
  };

  return (
    <div
      className="min-h-[100svh] bg-gradient-to-b from-[#FFF7EF] via-[#FFF3E8] to-[#DCF3EC]"
      data-testid="quiz-page"
    >
      {/* ── Top bar ─────────────────────────────────────── */}
      <div className="mx-auto flex max-w-2xl items-center justify-between px-4 py-3 sm:px-0 sm:pt-6">
        <button
          onClick={() => navigate(planId ? `/vocab/child/${childId}/plans` : `/vocab/child/${childId}`)}
          aria-label="Back"
          className="flex h-11 w-11 items-center justify-center rounded-full bg-card/85 text-foreground shadow-soft backdrop-blur"
          data-testid="quiz-back-btn"
        >
          <ArrowLeft size={20} weight="bold" />
        </button>

        <div className="flex items-center gap-2.5">
          {practiceMode && (
            <BloomBadge variant="primary" className="flex items-center gap-1 px-3 py-1 text-xs">
              <ArrowClockwise size={11} weight="bold" /> Practice
            </BloomBadge>
          )}
          {isRevision && !practiceMode && (
            <BloomBadge variant="teal" className="flex items-center gap-1 px-3 py-1 text-xs">
              🔁 Revision
            </BloomBadge>
          )}
          {planId && !practiceMode && !isRevision && (
            <BloomBadge variant="primary" className="flex items-center gap-1 px-3 py-1 text-xs">
              <Target size={11} weight="fill" /> Plan
            </BloomBadge>
          )}
          <BloomBadge variant={meta.badge} className="px-3 py-1 text-xs">
            {meta.label}
          </BloomBadge>
          <span
            className="rounded-full bg-card/85 px-4 py-2 font-display text-sm font-black text-foreground shadow-soft backdrop-blur"
            data-testid="quiz-progress"
          >
            {idx + 1} / {total}
          </span>
        </div>

        {streak >= 2 ? (
          <motion.span
            key={streak}
            initial={{ scale: 0.5 }}
            animate={{ scale: 1 }}
            className="flex items-center gap-1 rounded-full bg-secondary/25 px-3 py-2 text-sm font-extrabold text-secondary-ink shadow-soft"
          >
            <Lightning size={14} weight="fill" /> {streak}
          </motion.span>
        ) : (
          <span className="w-11" />
        )}
      </div>

      {/* ── Progress bar ────────────────────────────────── */}
      <div className="mx-auto max-w-2xl px-4 sm:px-0">
        <div className="mt-1 h-2 w-full overflow-hidden rounded-full bg-border/60">
          <motion.div
            className="h-full rounded-full bg-primary"
            initial={false}
            animate={{
              width: `${((idx + (phase === "feedback" ? 1 : 0)) / total) * 100}%`,
            }}
            transition={{ duration: 0.4, ease: "easeOut" }}
          />
        </div>
      </div>

      {/* ── Question card ───────────────────────────────── */}
      <main className="mx-auto max-w-2xl px-4 pb-12 sm:px-0">
        <AnimatePresence mode="wait">
          <motion.div
            key={idx}
            initial={{ opacity: 0, x: 30 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: -30 }}
            transition={{ duration: 0.25 }}
            className="mt-4 relative rounded-[2rem] border border-border bg-card p-6 shadow-soft-lg sm:p-8"
            data-testid="quiz-card"
          >
            {/* Source marker — ⬢ shown only for bank questions */}
            {question?.source === "bank" && (
              <span className="absolute top-4 right-5 text-primary text-sm leading-none select-none" aria-hidden="true">⬢</span>
            )}

            {/* Word display (meaning / synonym / antonym) */}
            {!isSentence && (
              <div className="mb-5 flex items-center justify-center gap-3">
                <h2
                  className="font-display text-3xl font-black capitalize tracking-tight text-foreground sm:text-4xl"
                  data-testid="quiz-word"
                >
                  {question.word}
                </h2>
                <button
                  onClick={() => speakWord(question.word)}
                  aria-label="Say the word"
                  className="flex h-11 w-11 items-center justify-center rounded-full bg-primary text-primary-foreground shadow-glow transition active:scale-90"
                  data-testid="quiz-speak-btn"
                >
                  <SpeakerHigh size={20} weight="fill" />
                </button>
              </div>
            )}

            {/* Question prompt */}
            {isSentence ? (
              <p
                className="mb-5 text-center font-display text-lg font-bold leading-relaxed text-foreground sm:text-xl"
                data-testid="quiz-prompt"
              >
                <BlankSentence text={question.prompt} />
              </p>
            ) : (
              <p
                className="mb-5 text-center text-base font-semibold text-muted-foreground"
                data-testid="quiz-prompt"
              >
                {question.prompt}
              </p>
            )}

            {/* Options — single-column for long text, 2-col for short words */}
            <div
              className={
                isLongOption || question.skill === "meaning"
                  ? "flex flex-col gap-3"
                  : "grid grid-cols-1 sm:grid-cols-2 gap-3"
              }
            >
              {question.options.map((opt, i) => (
                <OptionButton
                  key={`${idx}-${i}`}
                  label={LABELS[i]}
                  text={opt}
                  index={i}
                  state={getState(i)}
                  onClick={() => handleSelect(i)}
                />
              ))}
            </div>

            {/* Feedback banner */}
            <AnimatePresence>
              {phase === "feedback" && (
                <motion.div
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0 }}
                  className={cn(
                    "mt-5 flex items-center justify-center gap-2 rounded-2xl px-4 py-3 text-center font-display text-sm font-bold sm:text-base",
                    selected === question.correct
                      ? "bg-teal/12 text-teal-ink"
                      : "bg-destructive/10 text-destructive-ink",
                  )}
                  data-testid="quiz-feedback"
                >
                  {selected === question.correct ? (
                    <>
                      <Sparkle size={18} weight="fill" />
                      {streak >= 3
                        ? `${streak} in a row! 🔥`
                        : pickRandom(ENCOURAGEMENTS)}
                    </>
                  ) : (
                    <>
                      <ArrowRight size={18} weight="bold" />
                      {pickRandom(NEAR_MISSES)}
                    </>
                  )}
                </motion.div>
              )}
            </AnimatePresence>

            {/* ── Quiz Help section — wrong answer only, when enabled ── */}
            {helpEnabled && phase === "feedback" && selected !== null && selected !== question?.correct && (() => {
              const libraryWords = (question.option_words || []).filter(Boolean);
              return (
                <motion.div
                  initial={{ opacity: 0, y: 8 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.25 }}
                  className="mt-5 space-y-3"
                >
                  <p className="text-[11px] font-extrabold uppercase tracking-wider text-muted-foreground">
                    🔍 Explore these words
                  </p>
                  <div className="flex flex-wrap gap-2">
                    {question.options.map((opt, i) => {
                      const wordData = question.option_words?.[i];
                      return wordData ? (
                        <button
                          key={i}
                          onClick={() => {
                            const libIdx = libraryWords.findIndex((w) => w.word_id === wordData.word_id);
                            setHelpInitIdx(Math.max(0, libIdx));
                            setHelpOpen(true);
                          }}
                          className="rounded-full bg-primary/10 px-3.5 py-2 text-sm font-extrabold text-primary-strong transition-colors hover:bg-primary/20 active:scale-[0.97]"
                        >
                          {opt}
                        </button>
                      ) : (
                        <span
                          key={i}
                          title="Not in your word library yet"
                          className="cursor-default rounded-full bg-muted px-3.5 py-2 text-sm font-semibold text-muted-foreground opacity-60"
                        >
                          {opt}
                        </span>
                      );
                    })}
                  </div>

                  {/* Manual next button */}
                  <button
                    onClick={handleNext}
                    className="flex items-center gap-1.5 pt-1 font-display text-sm font-extrabold text-primary-strong hover:underline"
                    data-testid="quiz-next-btn"
                  >
                    Next question <ArrowRight size={14} weight="bold" />
                  </button>
                </motion.div>
              );
            })()}
          </motion.div>
        </AnimatePresence>
      </main>

      {/* ── Quiz Help Sheet ── */}
      <AnimatePresence>
        {helpOpen && question && (() => {
          const libraryWords = (question.option_words || []).filter(Boolean);
          return libraryWords.length > 0 ? (
            <QuizHelpSheet
              words={libraryWords}
              initialIndex={helpInitIdx}
              onClose={() => setHelpOpen(false)}
            />
          ) : null;
        })()}
      </AnimatePresence>
    </div>
  );
}